import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound } from "lucide-react";
import { authForgot, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await authForgot(email);
      setResult(r);
      toast.success("If an account exists, we've sent a reset link.");
    } catch (err) {
      toast.error(formatApiError(err, "Could not create reset link."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-page" className="max-w-md mx-auto px-6 py-20">
      <Link to="/login" data-testid="forgot-back-link" className="text-xs text-[#a0a0b0] hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to sign in
      </Link>
      <div className="cc-overline inline-flex items-center gap-2">
        <KeyRound size={11} /> Password recovery
      </div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-2">
        Forgot your <span className="text-[#00ff88]">password?</span>
      </h1>
      <p className="text-sm text-[#a0a0b0] mb-8">
        Enter your email and we&apos;ll generate a reset link. Follow it to choose a new password.
      </p>

      <form onSubmit={submit} className="cc-surface rounded-2xl p-7 space-y-5">
        <div>
          <div className="cc-overline mb-1.5">Email</div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]"><Mail size={14} /></span>
            <input
              data-testid="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@studio.gg"
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring"
              required
            />
          </div>
        </div>

        <button
          data-testid="forgot-submit"
          disabled={loading}
          className="cc-btn-primary w-full rounded-lg px-6 py-2.5 text-sm disabled:opacity-50"
        >
          {loading ? "Generating…" : "Send reset link"}
        </button>
      </form>

      {result && (
        <div
          data-testid="forgot-result"
          className="mt-5 cc-surface rounded-xl p-5 border border-[#00ff88]/20"
        >
          <div className="text-sm text-white mb-2">
            If an account exists with that email, we sent a reset link.
          </div>
          {result.dev_token ? (
            <>
              <div className="text-[11px] text-[#606070] uppercase tracking-widest mb-1">
                Dev reset link (no email provider)
              </div>
              <Link
                to={result.reset_path}
                data-testid="forgot-reset-link"
                className="text-[#00ff88] text-xs font-mono break-all hover:underline"
              >
                {result.reset_path}
              </Link>
            </>
          ) : (
            <div className="text-xs text-[#a0a0b0]">Check your inbox in a minute.</div>
          )}
        </div>
      )}
    </div>
  );
}
